import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { getAzureAdConfig } from './azure-ad-env';
import { clearMsalBrowserInteractionLock } from './msal-browser-cleanup';

/**
 * Initializes MSAL and processes the redirect response on cold start.
 * Must run before other app code that depends on `AuthService.isAuthenticated` for Entra sessions.
 */
export function msalAppInitializer(): () => Promise<void> {
  return () => {
    const ad = getAzureAdConfig();
    if (!ad) {
      return Promise.resolve();
    }
    const msal = inject(MsalService);
    const auth = inject(AuthService);

    return firstValueFrom(
      msal.initialize().pipe(
        concatMap(() => msal.handleRedirectObservable()),
        concatMap((result) => {
          if (result?.account) {
            msal.instance.setActiveAccount(result.account);
          } else {
            const accounts = msal.instance.getAllAccounts();
            if (accounts.length > 0 && !msal.instance.getActiveAccount()) {
              msal.instance.setActiveAccount(accounts[0]);
            }
          }
          auth.syncAzureSessionState();
          /** Just completed Entra redirect → always GET get_user_details. Otherwise use session if email matches. */
          if (result != null && result.account != null) {
            return auth.fetchSsoUserDetailsAfterInteractiveLogin();
          }
          return auth.ensureSsoUserDetailsFromSessionOrApi();
        })
      )
    )
      .then(() => undefined)
      .catch(() => {
        /** Broken redirect / hash can leave MSAL’s interaction lock set; clear so “Sign in with Microsoft” works. */
        clearMsalBrowserInteractionLock();
        auth.syncAzureSessionState();
        return firstValueFrom(auth.ensureSsoUserDetailsFromSessionOrApi());
      })
      .then(() => undefined);
  };
}
